import { useState } from 'react'
import { Alert, Box, Checkbox, FormControlLabel, FormGroup, Stack, TextField, Typography } from '@mui/material'
import { ConfirmDialog } from '@components/common/ConfirmDialog'
import type { AdminJobDto } from '@api/types/adminJobs'
import { apiErrorMessage, useStartJob } from '../hooks/useAdminJobs'
import { buildRecomputeRequest, MAX_BACKFILL_DAYS } from '../popularTimes'

interface RunJobDialogProps {
  job: AdminJobDto
  /** Parameters of a previous run ("Run again"), used to prefill the form. */
  initialParameters?: Record<string, unknown> | null
  onClose: () => void
  onStarted: (jobId: string, job: AdminJobDto) => void
}

const isoDay = (v: unknown): string => (typeof v === 'string' && v.length >= 10 ? v.slice(0, 10) : '')

/** "Run now" confirmation. Jobs with parameters get their small form here; everything else is a plain confirm. */
export function RunJobDialog({ job, initialParameters, onClose, onStarted }: RunJobDialogProps) {
  const start = useStartJob()
  const p = initialParameters ?? {}

  // popularity-recompute
  const [from, setFrom] = useState(isoDay(p.from))
  // `to` is stored exclusive (next midnight); show the inclusive day.
  const [to, setTo] = useState(() => {
    const raw = isoDay(p.to)
    if (!raw) return ''
    const d = new Date(`${raw}T00:00:00Z`)
    d.setUTCDate(d.getUTCDate() - 1)
    return d.toISOString().slice(0, 10)
  })
  const [typical, setTypical] = useState(p.typical !== false)
  const [scores, setScores] = useState(p.scores !== false)
  // statistics-rebuild
  const [startDate, setStartDate] = useState(typeof p.startDate === 'string' ? p.startDate : '')
  const [endDate, setEndDate] = useState(typeof p.endDate === 'string' ? p.endDate : '')
  // Which parts to rebuild; absent (older runs, the nightly one) means all three.
  const [users, setUsers] = useState(p.users !== false)
  const [adventures, setAdventures] = useState(p.adventures !== false)
  const [groups, setGroups] = useState(p.groups !== false)
  const [error, setError] = useState<string | null>(null)

  const confirm = async () => {
    setError(null)
    let parameters: object | undefined
    if (job.key === 'popularity-recompute') {
      const built = buildRecomputeRequest(from, to, typical, scores)
      if (built.error) return setError(built.error)
      parameters = built.request
    } else if (job.key === 'statistics-rebuild') {
      if (startDate && endDate && startDate > endDate) return setError('The start date must be on or before the end date.')
      if (!users && !adventures && !groups) return setError('Choose at least one of users, adventures or groups.')
      parameters = { startDate: startDate || undefined, endDate: endDate || undefined, users, adventures, groups }
    }
    try {
      const started = await start.mutateAsync({ key: job.key, parameters })
      onStarted(started.jobId, job)
      onClose()
    } catch (e) {
      setError(apiErrorMessage(e, 'The job could not be started.'))
    }
  }

  return (
    <ConfirmDialog
      open
      title={`Run ${job.name}?`}
      message={job.description}
      confirmLabel="Run now"
      loading={start.isPending}
      onConfirm={confirm}
      onClose={onClose}
    >
      <Box sx={{ mt: 2 }}>
        {job.key === 'popularity-recompute' && (
          <Stack spacing={2}>
            <Typography variant="body2" color="text.secondary">
              Leave the dates empty to rebuild the usual recent window. A backfill range covers whole UTC days, at most {MAX_BACKFILL_DAYS} days.
            </Typography>
            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
              <TextField type="date" label="From (UTC)" size="small" value={from} onChange={(e) => setFrom(e.target.value)} InputLabelProps={{ shrink: true }} fullWidth />
              <TextField type="date" label="To (UTC, inclusive)" size="small" value={to} onChange={(e) => setTo(e.target.value)} InputLabelProps={{ shrink: true }} fullWidth />
            </Stack>
            <FormGroup row>
              <FormControlLabel control={<Checkbox checked={typical} onChange={(e) => setTypical(e.target.checked)} size="small" />} label="Typical weeks" />
              <FormControlLabel control={<Checkbox checked={scores} onChange={(e) => setScores(e.target.checked)} size="small" />} label="Popularity scores" />
            </FormGroup>
          </Stack>
        )}
        {job.key === 'statistics-rebuild' && (
          <Stack spacing={2}>
            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
              <TextField type="date" label="Start date" size="small" value={startDate} onChange={(e) => setStartDate(e.target.value)} InputLabelProps={{ shrink: true }} fullWidth helperText="Default: 31 days ago" />
              <TextField type="date" label="End date" size="small" value={endDate} onChange={(e) => setEndDate(e.target.value)} InputLabelProps={{ shrink: true }} fullWidth helperText="Default: yesterday" />
            </Stack>
            <FormGroup row>
              <FormControlLabel control={<Checkbox checked={users} onChange={(e) => setUsers(e.target.checked)} size="small" />} label="Users" />
              <FormControlLabel control={<Checkbox checked={adventures} onChange={(e) => setAdventures(e.target.checked)} size="small" />} label="Adventures" />
              <FormControlLabel control={<Checkbox checked={groups} onChange={(e) => setGroups(e.target.checked)} size="small" />} label="Groups" />
            </FormGroup>
          </Stack>
        )}
        {error && <Alert severity="error" sx={{ mt: 2 }}>{error}</Alert>}
      </Box>
    </ConfirmDialog>
  )
}
